// Deposit-zap PURE LOGIC — no DOM, no ethers, no window. Importable in Node for
// unit tests (zap-core.test.mjs) AND in the browser by zap.js (execution layer).
//
// Model (JP, 2026-06-13): bridge any token/chain → the EXACT vault USDC + a HYPE
// gas sliver, into the USER'S OWN HyperEVM wallet, ONCE. Then deposits into any
// vault are normal cheap on-chain txs reusing that USDC + HYPE (no per-vault
// bridge). NO custom contract; LI.FI (li.quest) does the bridge+refuel.
//
// 🔴 THE load-bearing invariant: the route's destination token MUST equal the
// vault's exact USDC. A bridge can deliver a *different* USDC representation
// (USD₮0, etc.) → the app shows $0 and funds look lost (the friend's bug).
// assertDestTokenIsVaultUsdc() is the first assertion in every test layer.

// Checksummed — LI.FI's /v1/quote 404s ("could not find token", code 1003) on a
// lowercase address. Always request with this exact casing.
export const VAULT_USDC = "0xb88339CB7199b77E23DB6E890353E22632Ba630f";
export const HYPEREVM_CHAIN_ID = 999;
export const HYPE_NATIVE = "0x0000000000000000000000000000000000000000";
export const LIFI_BASE = "https://li.quest/v1";

// Refuel (HYPE-for-gas) sizing. LI.FI recommends ~$0.044 of HYPE per tx of
// headroom on 999; $0.30 ≈ ~7 txs — several deposits. The in-route refuel is a
// GasZip step with a real per-route ceiling (empirically $0.60 → "no available
// quotes" while $0.30 succeeds), so we TARGET low and CAP below the cliff. A
// heavy multi-vault user tops up via a same-chain USDC→HYPE swap later (no
// ceiling — it's a DEX); the refuel only needs to cover the FIRST txs, because
// you can't swap-for-gas with zero gas.
export const REFUEL_TARGET_USD = 0.30;
export const REFUEL_CAP_USD = 0.50;
export const DEFAULT_SLIPPAGE = 0.005; // 0.5%
// Reject a quote whose total fees eat more than this fraction of the deposit
// (dust protection) — a $5 deposit shouldn't lose 30% to fees.
export const MAX_FEE_FRACTION = 0.10;
// Below this fee fraction it's fine; between WARN and MAX we still allow but warn
// loudly (show the %, nudge a bigger amount / cheaper chain); at/above MAX we block.
export const FEE_WARN_FRACTION = 0.02;

const _norm = (a) => (a || "").toLowerCase();
export function isVaultUsdc(addr) { return _norm(addr) === _norm(VAULT_USDC); }

// Which path applies given where the user's funds already are.
//   'deposit-direct'  — already on HyperEVM WITH usable HYPE → no zap at all.
//   'gas-swap-only'   — already on HyperEVM, has USDC but NO HYPE → same-chain
//                       USDC→HYPE swap (user's money), then deposit.
//   'bridge'          — funds on another chain → bridge USDC + refuel HYPE.
export function selectPath({ fromChainId, hasNativeUsdc, hasHype }) {
  if (fromChainId === HYPEREVM_CHAIN_ID) {
    return hasHype ? "deposit-direct" : "gas-swap-only";
  }
  return "bridge";
}

// Source-token units of gas to request (LI.FI fromAmountForGas is in the FROM
// token). Targets REFUEL_TARGET_USD, capped at REFUEL_CAP_USD, converted via the
// source token's USD price. Returns a string of integer token units.
export function sizeRefuel({ fromTokenPriceUsd, fromTokenDecimals }) {
  const price = Number(fromTokenPriceUsd);
  const dec = Number(fromTokenDecimals);
  if (!isFinite(price) || price <= 0 || !isFinite(dec)) {
    throw new Error("sizeRefuel: bad price/decimals");
  }
  const usd = Math.min(REFUEL_TARGET_USD, REFUEL_CAP_USD);
  const tokenAmt = usd / price; // human units of the source token
  return BigInt(Math.floor(tokenAmt * 10 ** dec)).toString();
}

// Build the li.quest/v1/quote query params for a BRIDGE (any chain → 999 USDC +
// refuel into the user's own wallet). toAddress == fromAddress (funds to user).
export function buildBridgeQuoteParams({
  fromChain, fromToken, fromAmount, fromAddress, fromAmountForGas,
  slippage = DEFAULT_SLIPPAGE, order = "CHEAPEST",
}) {
  if (!fromChain || !fromToken || !fromAmount || !fromAddress) {
    throw new Error("buildBridgeQuoteParams: missing required field");
  }
  const p = {
    fromChain: String(fromChain),
    toChain: String(HYPEREVM_CHAIN_ID),
    fromToken: String(fromToken),
    toToken: VAULT_USDC,                 // CHECKSUMMED pin — the guard's request side
    fromAmount: String(fromAmount),
    fromAddress: String(fromAddress),
    toAddress: String(fromAddress),      // two-step: deliver to the user's own wallet
    slippage: String(slippage),
    // Favour the cheapest route. LI.FI's default "RECOMMENDED" returned a $0.51
    // Symbiosis route where a ~1.5¢ Across route existed (Layer-2, 2026-06-13);
    // on small deposits that's the difference between fine and 10%-fees-blocked.
    order: String(order),
  };
  if (fromAmountForGas) p.fromAmountForGas = String(fromAmountForGas); // refuel
  return p;
}

// Same-chain (999) USDC→HYPE swap params, for the gas-swap-only path.
export function buildGasSwapQuoteParams({ fromAddress, usdcAmount, slippage = DEFAULT_SLIPPAGE }) {
  if (!fromAddress || !usdcAmount) throw new Error("buildGasSwapQuoteParams: missing field");
  return {
    fromChain: String(HYPEREVM_CHAIN_ID),
    toChain: String(HYPEREVM_CHAIN_ID),
    fromToken: VAULT_USDC,
    toToken: HYPE_NATIVE,
    fromAmount: String(usdcAmount),
    fromAddress: String(fromAddress),
    toAddress: String(fromAddress),
    slippage: String(slippage),
  };
}

// ── TWO-ROUTE GAS LEG (the robust gas delivery) ──────────────────────────────
// Instead of bolting gas onto the USDC route via LI.FI's GasZip `fromAmountForGas`
// "refuel" add-on (a single fragile mechanism — 100% down to 999 on 2026-06-15),
// deliver gas as ITS OWN Across route: a sliver of source USDC → native HYPE on
// 999. Verified live: $0.60 → ~$0.59 HYPE via Across, ~1%, seconds, ETH/ARB/OP/
// Base. Same battle-tested bridge as the USDC leg, no GasZip dependency. The user
// signs two bridges (funds + gas) ONCE — fund-once amortizes it. Full rationale:
// docs/architecture/DEPOSIT_ZAP_RUNBOOK.md §5.
//
// Source USD to spend on the gas sliver. ~$0.59 of HYPE out ≈ ~13 txs of headroom
// (LI.FI ~$0.044/tx) — covers several deposits + a withdrawal. Across's small-
// transfer floor sits near here ($0.50 fell back to a pricier tool; $0.60 routes
// Across cleanly), so don't size it below ~$0.60.
export const GAS_TARGET_USD = 0.60;
export const GAS_SLIPPAGE = 0.01; // a tiny gas sliver tolerates more slippage than the bulk

// Source-token units to bridge for the gas sliver (targets GAS_TARGET_USD).
export function sizeGasLeg({ fromTokenPriceUsd = 1.0, fromTokenDecimals = 6 } = {}) {
  const price = Number(fromTokenPriceUsd), dec = Number(fromTokenDecimals);
  if (!isFinite(price) || price <= 0 || !isFinite(dec)) throw new Error("sizeGasLeg: bad price/decimals");
  return BigInt(Math.floor((GAS_TARGET_USD / price) * 10 ** dec)).toString();
}

// Gas-leg quote params: source token → NATIVE HYPE on 999 (toToken = native).
export function buildGasLegParams({ fromChain, fromToken, fromAmount, fromAddress, slippage = GAS_SLIPPAGE, order = "CHEAPEST" }) {
  if (!fromChain || !fromToken || !fromAmount || !fromAddress) throw new Error("buildGasLegParams: missing required field");
  return {
    fromChain: String(fromChain),
    toChain: String(HYPEREVM_CHAIN_ID),
    fromToken: String(fromToken),
    toToken: HYPE_NATIVE,               // native HYPE — the gas asset
    fromAmount: String(fromAmount),
    fromAddress: String(fromAddress),
    toAddress: String(fromAddress),     // to the user's own wallet
    slippage: String(slippage),
    order: String(order),
  };
}

// Split a total source amount into (usdc-leg, gas-leg). Guards that the gas sliver
// can't swallow the whole deposit.
export function splitDepositForGas({ totalWei, gasLegWei }) {
  const total = BigInt(totalWei), gas = BigInt(gasLegWei);
  if (gas <= 0n) throw new Error("splitDepositForGas: gas sliver must be > 0");
  if (gas >= total) throw new Error("Deposit too small to also cover the ~$0.60 gas sliver — bridge a bit more.");
  return { usdcLegWei: (total - gas).toString(), gasLegWei: gas.toString() };
}

// 🔴 THE GUARD. Given a LI.FI quote response, assert its destination token is the
// exact vault USDC. Throws ZapWrongTokenError otherwise. Case-insensitive on the
// RESPONSE (LI.FI may return checksummed or not).
export class ZapWrongTokenError extends Error {
  constructor(got) {
    super(`Route would deliver the wrong token (${got || "unknown"}), not the vault USDC (${VAULT_USDC}). Refusing to protect your funds.`);
    this.name = "ZapWrongTokenError";
    this.got = got;
  }
}
export function assertDestTokenIsVaultUsdc(quote) {
  const got = quote && quote.action && quote.action.toToken && quote.action.toToken.address;
  if (!isVaultUsdc(got)) throw new ZapWrongTokenError(got);
  return true;
}

// 🔴 THE GAS-LEG GUARD. Native HYPE is unambiguous (one native coin), so this is
// simpler than the USDC guard but still load-bearing: assert the gas route lands
// NATIVE HYPE on 999, not some token. Accepts either native sentinel (0x000…000
// or 0xEeee…EEeE) and, when the response carries chainId, requires it to be 999.
const _NATIVE_SENTINELS = ["0x0000000000000000000000000000000000000000", "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee"];
export function isNativeHype(addr) { return _NATIVE_SENTINELS.includes(_norm(addr)); }
export class ZapWrongGasTokenError extends Error {
  constructor(got) {
    super(`Gas route would deliver ${got || "unknown"}, not native HYPE on HyperEVM. Refusing.`);
    this.name = "ZapWrongGasTokenError";
    this.got = got;
  }
}
export function assertDestTokenIsNativeHype(quote) {
  const tok = quote && quote.action && quote.action.toToken;
  const addr = tok && tok.address;
  const chainOk = !tok || tok.chainId == null || Number(tok.chainId) === HYPEREVM_CHAIN_ID;
  if (!isNativeHype(addr) || !chainOk) throw new ZapWrongGasTokenError(addr);
  return true;
}

// Validate the gas-leg quote (guard FIRST, then usable output). Never throws.
export function validateGasLeg(quote) {
  try {
    assertDestTokenIsNativeHype(quote);
  } catch (e) {
    if (e instanceof ZapWrongGasTokenError) return { ok: false, reason: "wrong-gas-token", message: e.message, got: e.got };
    throw e;
  }
  const out = Number((quote && quote.estimate && quote.estimate.toAmount) || 0);
  if (!(out > 0)) return { ok: false, reason: "no-gas-output", message: "Gas route returns no HYPE." };
  return { ok: true, hypeOut: quote.estimate.toAmount };
}

// Parse + LABEL fees. Bridge/LI.FI fees are the bridge's, NOT ours; our fee = 0.
// Returns { bridgeFeeUsd, gasUsd, ourFeeUsd, totalCostUsd, label }.
export function parseFees(quote) {
  const est = (quote && quote.estimate) || {};
  const feeCosts = est.feeCosts || [];
  const gasCosts = est.gasCosts || [];
  const bridgeFeeUsd = feeCosts.reduce((s, f) => s + (parseFloat(f.amountUSD) || 0), 0);
  const gasUsd = gasCosts.reduce((s, g) => s + (parseFloat(g.amountUSD) || 0), 0);
  return {
    bridgeFeeUsd: round2(bridgeFeeUsd),
    gasUsd: round2(gasUsd),
    ourFeeUsd: 0,                                    // CrystalClear takes nothing on the zap
    totalCostUsd: round2(bridgeFeeUsd + gasUsd),
    label: "Bridge fee is the bridge's, not CrystalClear's.",
  };
}
function round2(n) { return Math.round(n * 100) / 100; }

// Full validation gate: guard FIRST, then dust/min, then fee bound. Returns
// { ok, reason }. Never throws (callers branch on .ok); the guard failure is
// surfaced as reason="wrong-token" so the UI can show the protective message.
export function validateQuote(quote, { depositUsd, minDepositUsd = 0, minReceiveUsd = 0, maxFeeFraction = MAX_FEE_FRACTION } = {}) {
  try {
    assertDestTokenIsVaultUsdc(quote);
  } catch (e) {
    if (e instanceof ZapWrongTokenError) return { ok: false, reason: "wrong-token", message: e.message, got: e.got };
    throw e;
  }
  const est = (quote && quote.estimate) || {};
  const toMin = Number(est.toAmountMin || 0);
  if (!(toMin > 0)) return { ok: false, reason: "no-output", message: "Route returns no usable output." };
  // G2: vault-min pre-check on the RECEIVED amount (what actually gets deposited),
  // so a user can't bridge then fail the deposit's on-chain minimum.
  const receiveUsd = toMin / 1e6;
  if (minReceiveUsd > 0 && receiveUsd < minReceiveUsd) {
    return { ok: false, reason: "below-vault-min", message: `After fees you'd receive ~$${receiveUsd.toFixed(2)}, under this vault's $${minReceiveUsd} minimum — bridge a bit more.`, receiveUsd };
  }
  const fees = parseFees(quote);
  if (depositUsd != null) {
    if (minDepositUsd > 0 && depositUsd < minDepositUsd) {
      return { ok: false, reason: "below-min", message: `Minimum is $${minDepositUsd}.` };
    }
    if (fees.totalCostUsd >= depositUsd * maxFeeFraction) {
      return { ok: false, reason: "fees-too-high", message: `Fees ($${fees.totalCostUsd}) are too large for this amount — try a larger deposit.` };
    }
  }
  return { ok: true, fees, toAmountMin: est.toAmountMin, receiveUsd };
}

// G3: tiered fee assessment for the review screen. Pure — feed it the parsed fee
// total + the deposit size. Returns the transparency numbers + a tier:
//   'ok'    fees < FEE_WARN_FRACTION
//   'warn'  FEE_WARN_FRACTION ≤ fees < MAX_FEE_FRACTION  (allow, but warn + nudge)
//   'block' fees ≥ MAX_FEE_FRACTION                      (validateQuote blocks it)
// `suggestedMinDeposit` = the (rounded-up) amount at which fees drop under the warn
// line, since bridge fees are ~fixed regardless of size.
export function assessFees({ totalCostUsd, depositUsd }) {
  const cost = Number(totalCostUsd) || 0;
  const dep = Number(depositUsd) || 0;
  const frac = dep > 0 ? cost / dep : 0;
  const tier = frac >= MAX_FEE_FRACTION ? "block" : frac >= FEE_WARN_FRACTION ? "warn" : "ok";
  const suggestedMinDeposit = cost > 0 ? Math.ceil(cost / FEE_WARN_FRACTION) : 0;
  return { tier, totalCostUsd: cost, fractionOfDeposit: frac, pct: Math.round(frac * 1000) / 10, suggestedMinDeposit };
}

// LI.FI quote responses include `transactionRequest` (the tx to send) only when
// fromAddress is supplied. Surface a clear error if absent (mis-built request).
export function extractTxRequest(quote) {
  const tx = quote && quote.transactionRequest;
  if (!tx || !tx.to || !tx.data) throw new Error("Quote has no transactionRequest — cannot execute.");
  return tx;
}
